import facepaint from 'facepaint';
import { palette } from './palette/index.js';
import { UserExperienceGroup } from './lib/auth/types.js';

/** @deprecated Use `palette` instead **/ const color = palette;
const breakpoints = [
    "small",
    "medium",
    "large",
    "xlarge",
    "xxlarge"
];
const breakpoint = {
    /** < 640 */ small: 640,
    medium: 960,
    large: 1280,
    xlarge: 1440,
    xxlarge: 1720
};
// fontFamily
const FONTSTACK_FALLBACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';
const INTER_FONTSTACK = `"Inter", ${FONTSTACK_FALLBACK}`;
const MONO_FONTSTACK = '"Geist Mono", monospace';
const fontFamily = {
    display: INTER_FONTSTACK,
    body: INTER_FONTSTACK,
    mono: MONO_FONTSTACK
};
const scales = [
    "small",
    "medium",
    "large"
];
// control heights
const controlHeight = {
    small: 28,
    medium: 36,
    large: 40
};
// lineHeight
const lineHeight = {
    default: 1.3,
    heading: 1.125,
    subheading: 1.333,
    body: 1.618
};
// type scale — ~1.2 — doubles every 4th size
const scale = {
    s1: 10,
    s2: 12,
    s3: 14,
    s4: 16,
    s5: 20,
    s6: 24,
    s7: 28,
    s8: 32,
    s9: 40,
    s10: 48,
    s11: 56,
    s12: 64
};
// fontSize — doubles every 4th size
const size = {
    ...scale,
    micro: scale.s1,
    small: scale.s2,
    base: scale.s3,
    h6: scale.s3,
    h5: scale.s4,
    h4: scale.s5,
    h3: scale.s6,
    h2: scale.s7,
    h1: scale.s10
};
// borders
const borderWidth = {
    hairline: "var(--hairline-border-width)"
};
const borderStyles = {
    hairline: `${borderWidth.hairline} solid ${palette.gray8}`
};
// margin and padding
const space = {
    s0: 0,
    s0_5: 2,
    s1: 4,
    s1_5: 6,
    s2: 8,
    s2_5: 12,
    s3: 16,
    s3_5: 24,
    s4: 32,
    s4_5: 48,
    s5: 64,
    s5_5: 96,
    s6: 128,
    s6_5: 192,
    s7: 256,
    s7_5: 384,
    s8: 512,
    auto: "auto"
};
// animation speeds
const animationSpeed = {
    fast: "100ms",
    medium: "150ms",
    slow: "250ms"
};
const transition = `border ${animationSpeed.fast} ease, background ${animationSpeed.fast} ease, box-shadow ${animationSpeed.medium} ease`;
// media queries at breakpoints
// check out https://github.com/emotion-js/facepaint for usage instructions
const withMediaQueries = facepaint([
    `@media(min-width: ${breakpoint.small}px)`,
    `@media(min-width: ${breakpoint.medium}px)`,
    `@media(min-width: ${breakpoint.large}px)`,
    `@media(min-width: ${breakpoint.xlarge}px)`,
    `@media(min-width: ${breakpoint.xxlarge}px)`
]);
// container size queries at breakpoints
// check out https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_size_and_style_queries for usage instructions
const withContainerQueries = facepaint([
    `@container(min-width: ${breakpoint.small}px)`,
    `@container(min-width: ${breakpoint.medium}px)`,
    `@container(min-width: ${breakpoint.large}px)`,
    `@container(min-width: ${breakpoint.xlarge}px)`,
    `@container(min-width: ${breakpoint.xxlarge}px)`
]);
const withDarkModeStyles = (styles)=>{
    return {
        "@media (prefers-color-scheme: dark)": styles,
        "[data-color-scheme=dark] &": styles
    };
};
const withLightModeStyles = (styles)=>{
    return {
        ...styles,
        "[data-color-scheme=light] &": styles
    };
};
const responsivePageMargins = [
    space.s3,
    space.s4
];
// heights
const largeScreenNavigationHeight = 54;
const smallScreenNavigationHeight = 108;
const responsiveNavigationHeight = [
    smallScreenNavigationHeight,
    smallScreenNavigationHeight,
    largeScreenNavigationHeight,
    largeScreenNavigationHeight,
    largeScreenNavigationHeight
];
const meanings = [
    "neutral",
    "primary",
    "danger"
];
const prominences = [
    "low",
    "medium",
    "high"
];
const DEFAULT_TIMEZONE = "local";
const DEFAULT_DATE_FORMAT = "MM/dd/yyyy";
const DEFAULT_TIME_FORMAT = "h:mm aa";
const ACCOUNT_QUERY_PARAMETER_KEY = "account";
const shadow = (type)=>{
    switch(type){
        case "small":
        case "tooltip":
            return {
                boxShadow: "var(--shadow-small)",
                border: borderStyles.hairline
            };
        case "medium":
        case "menu":
            return {
                boxShadow: "var(--shadow-medium)",
                border: borderStyles.hairline
            };
        case "large":
        case "modal":
            return {
                boxShadow: "var(--shadow-large)",
                border: borderStyles.hairline
            };
    }
};
// Healthcare
const healthcareUXGroups = [
    UserExperienceGroup.Clearinghouse,
    UserExperienceGroup.EligibilityOnly,
    UserExperienceGroup.EdiPlatformWithHealthcare,
    UserExperienceGroup.EdiPlatformWithClaims
];

export { ACCOUNT_QUERY_PARAMETER_KEY, DEFAULT_DATE_FORMAT, DEFAULT_TIMEZONE, DEFAULT_TIME_FORMAT, animationSpeed, borderStyles, borderWidth, breakpoint, breakpoints, color, controlHeight, fontFamily, healthcareUXGroups, largeScreenNavigationHeight, lineHeight, meanings, palette, prominences, responsiveNavigationHeight, responsivePageMargins, scales, shadow, size, smallScreenNavigationHeight, space, transition, withContainerQueries, withDarkModeStyles, withLightModeStyles, withMediaQueries };
